import { useEffect, useRef } from "react";
import { useLocation } from "wouter";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const SID_KEY = "llc_sid";
const VID_KEY = "llc_vid";
const UTM_KEY = "llc_utm";

export type Utm = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

/**
 * True when the page is being rendered purely as a preview (e.g. inside the
 * admin heatmap backdrop iframe, opened with `?notrack=1`). In that mode we
 * must NOT fire pageviews/events/replay/heatmap capture, or the admin merely
 * viewing analytics would pollute the very data they're inspecting.
 */
export function isTrackingDisabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("notrack") === "1";
  } catch {
    return false;
  }
}

export function getSessionId(): string {
  let sid = sessionStorage.getItem(SID_KEY);
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(SID_KEY, sid);
  }
  return sid;
}

/**
 * Stable per-visitor id persisted in localStorage so it survives across sessions.
 * Used for consistent A/B bucketing — never tied to a name or any PII.
 */
export function getVisitorId(): string {
  try {
    let vid = localStorage.getItem(VID_KEY);
    if (!vid) {
      vid = "v_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(VID_KEY, vid);
    }
    return vid;
  } catch {
    // localStorage unavailable (private mode) — fall back to session-scoped id.
    return getSessionId();
  }
}

/**
 * First-touch UTM capture: the first time UTM params appear in the URL during a
 * session we persist them, so source attribution survives client-side SPA
 * navigation that strips the query string.
 */
function captureUtm(): Utm {
  if (typeof window === "undefined") return {};
  const existing = sessionStorage.getItem(UTM_KEY);
  if (existing) {
    try { return JSON.parse(existing) as Utm; } catch { /* fall through */ }
  }
  const params = new URLSearchParams(window.location.search);
  const utm: Utm = {};
  const src = params.get("utm_source");
  const med = params.get("utm_medium");
  const camp = params.get("utm_campaign");
  if (src) utm.utmSource = src.slice(0, 120);
  if (med) utm.utmMedium = med.slice(0, 120);
  if (camp) utm.utmCampaign = camp.slice(0, 120);
  if (Object.keys(utm).length > 0) {
    sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
  }
  return utm;
}

function getUtm(): Utm {
  if (typeof window === "undefined") return {};
  const existing = sessionStorage.getItem(UTM_KEY);
  if (existing) {
    try { return JSON.parse(existing) as Utm; } catch { return {}; }
  }
  return {};
}

/**
 * Run a low-priority callback once the browser is idle (with a short timeout
 * fallback so it still fires promptly, and a setTimeout path for browsers
 * without requestIdleCallback). Used to keep non-critical tracking beacons
 * (pageview, presence) off the initial critical path so they don't contend
 * with the LCP image, fonts and route chunks during first load.
 *
 * Returns a canceller for effect cleanup that FLUSHES the callback if it hasn't
 * run yet, so no beacon is ever dropped: on a fast route change/unmount the
 * pending call fires immediately (with keepalive) instead of being canceled.
 * An internal guard guarantees the callback runs at most once.
 */
function runWhenIdle(cb: () => void, timeout = 2000): () => void {
  if (typeof window === "undefined") {
    cb();
    return () => {};
  }
  let ran = false;
  const run = () => {
    if (ran) return;
    ran = true;
    cb();
  };
  const win = window as typeof window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (typeof win.requestIdleCallback === "function") {
    const id = win.requestIdleCallback(run, { timeout });
    return () => {
      win.cancelIdleCallback?.(id);
      run();
    };
  }
  const t = setTimeout(run, 200);
  return () => {
    clearTimeout(t);
    run();
  };
}

/**
 * Emit a booking-funnel event keyed to the anonymous session id and any UTM
 * captured on first landing. Fire-and-forget; never blocks the UI.
 */
export function trackEvent(eventType: string, metadata?: Record<string, unknown>): void {
  if (typeof window === "undefined" || isTrackingDisabled()) return;
  try {
    const sessionId = getSessionId();
    const visitorId = getVisitorId();
    const utm = getUtm();
    fetch("/api/analytics/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType,
        sessionId,
        visitorId,
        path: window.location.pathname,
        referrer: document.referrer || "",
        ...utm,
        // Stamp the client's wall clock at fire time. The session-replay story
        // panel anchors each beat with `clientTs - rrweb meta.startTime` (same
        // device clock as the recording), giving skew-free, upload-lag-free
        // alignment. Harmless extra key for every other consumer.
        metadata: { ...(metadata ?? {}), clientTs: Date.now() },
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* no-op */
  }
}

export function usePageTracking() {
  const [location] = useLocation();
  const lastTracked = useRef<string | null>(null);

  useEffect(() => {
    if (isTrackingDisabled()) return;
    if (lastTracked.current === location) return;
    lastTracked.current = location;

    const utm = captureUtm();

    // Fire GA4 page_view on every SPA route change
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location,
        page_location: window.location.href,
        page_title: document.title,
      });
    }

    const sessionId = getSessionId();
    const visitorId = getVisitorId();
    // Defer the first-party pageview beacon to browser idle so it doesn't
    // compete with the LCP image, fonts and route chunks during initial load.
    // It still fires (with keepalive) — just off the critical path.
    return runWhenIdle(() => {
      fetch("/api/analytics/pageview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          path: location,
          referrer: document.referrer || "",
          sessionId,
          visitorId,
          ...utm,
        }),
        keepalive: true,
      }).catch(() => {});
    });
  }, [location]);
}

/**
 * Detect on-page frustration and report it as funnel events, keyed to the same
 * anonymous session id used everywhere else (no PII, no keystrokes). Two signals:
 *  - rage_click: 3+ clicks in the same small area within ~1s.
 *  - dead_click: a click on a clickable-looking element (cursor:pointer) that
 *    isn't actually interactive and produces no DOM change or navigation.
 * Both are throttled so one frustrated burst emits at most one event.
 */
export function useFrustrationTracking(): void {
  useEffect(() => {
    if (typeof window === "undefined" || isTrackingDisabled()) return;

    let clickTimes: number[] = [];
    let lastX = 0;
    let lastY = 0;
    let rageFiredAt = 0;
    let deadFiredAt = 0;

    const isInteractive = (el: Element | null): boolean =>
      !!el?.closest(
        "a,button,input,select,textarea,label,summary,[role=button],[role=link],[contenteditable=true],[onclick]",
      );

    const onClick = (e: MouseEvent) => {
      const now = Date.now();

      // Rage: cluster clicks that stay within a small radius and time window.
      if (Math.abs(e.clientX - lastX) > 40 || Math.abs(e.clientY - lastY) > 40) {
        clickTimes = [];
      }
      lastX = e.clientX;
      lastY = e.clientY;
      clickTimes = clickTimes.filter((t) => now - t < 1000);
      clickTimes.push(now);
      if (clickTimes.length >= 3 && now - rageFiredAt > 3000) {
        rageFiredAt = now;
        clickTimes = [];
        trackEvent("rage_click", { path: window.location.pathname });
      }

      // Dead: a pointer-cursor element that isn't really interactive and does
      // nothing (no DOM mutation, no navigation) shortly after the click.
      const target = e.target as Element | null;
      if (
        target &&
        !isInteractive(target) &&
        now - deadFiredAt > 2000 &&
        window.getComputedStyle(target).cursor === "pointer"
      ) {
        const urlBefore = window.location.href;
        let mutated = false;
        const obs = new MutationObserver(() => {
          mutated = true;
        });
        obs.observe(document.documentElement, {
          childList: true,
          subtree: true,
          attributes: true,
          characterData: true,
        });
        window.setTimeout(() => {
          obs.disconnect();
          if (!mutated && window.location.href === urlBefore) {
            deadFiredAt = Date.now();
            trackEvent("dead_click", { path: window.location.pathname });
          }
        }, 700);
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}

/**
 * Heartbeat so the admin "who's online now" view knows this visitor is present.
 * Fires immediately and then every 15s while the tab is open. Anonymous
 * (session/visitor id + current path only) and suppressed for opted-out/host
 * devices, preview (?notrack=1) frames, and admin/host pages. Server-side state
 * is purely ephemeral (in-memory), so nothing is persisted.
 */
export function usePresenceHeartbeat(): void {
  const [location] = useLocation();
  useEffect(() => {
    if (typeof window === "undefined" || isTrackingDisabled()) return;
    try {
      if (localStorage.getItem("llc_replay_optout") === "1") return;
    } catch {
      /* ignore */
    }
    // Don't announce the host browsing their own admin/host tools as a visitor.
    if (location.startsWith("/admin") || location.startsWith("/hosthq")) return;

    const beat = () => {
      try {
        fetch("/api/presence", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: getSessionId(),
            visitorId: getVisitorId(),
            path: window.location.pathname,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch {
        /* no-op */
      }
    };
    // Defer the very first heartbeat to idle so it doesn't contend with the
    // initial page load; the recurring 15s beat is unchanged.
    const cancelIdle = runWhenIdle(beat);
    const id = window.setInterval(beat, 15000);
    return () => {
      cancelIdle();
      window.clearInterval(id);
    };
  }, [location]);
}
